import React, { useState, useEffect } from 'react';
import axios from 'axios';
import "../../styles/car-hire-details.css";

const CarHireOffers = () => {
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchOffers();
    }, []);

    const fetchOffers = async () => {
        try {
            const response = await axios.get('/api/car-hire/cars/?featured=true');
            // Fix: Handle paginated response structure
            const offersData = response.data.results || response.data || [];
            setOffers(Array.isArray(offersData) ? offersData.slice(0, 6) : []);
        } catch (error) {
            console.error('Error fetching car offers:', error);
            setOffers([]); // Set empty array on error
        } finally {
            setIsLoading(false);
        }
    };

    const formatPrice = (price) => {
        return parseFloat(price).toFixed(2);
    };

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating || 4.5);
        const hasHalfStar = (rating % 1) !== 0;
        
        for (let i = 1; i <= 5; i++) {
            if (i <= fullStars) {
                stars.push(<i key={i} className="fas fa-star text-warning"></i>);
            } else if (i === fullStars + 1 && hasHalfStar) {
                stars.push(<i key={i} className="fas fa-star-half-alt text-warning"></i>);
            } else {
                stars.push(<i key={i} className="fas fa-star text-muted"></i>);
            }
        }
        return stars;
    };

    if (isLoading) {
        return (
            <section className="car-offers-section">
                <div className="container">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="section__title text-center mb-5">
                                <h2>Featured Car Rental Offers</h2>
                            </div>
                            <div className="text-center loading-state">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                                <p>Loading amazing car deals...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="car-offers-section">
            <div className="container">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="section__title text-center mb-5">
                            <h2>Featured Car Rental Offers</h2>
                            <p>Discover amazing deals on car rentals from top companies</p>
                        </div>
                    </div>
                </div>
                
                <div className="row">
                    {offers.length > 0 ? offers.map((offer, index) => (
                        <div key={offer.id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                            <div className="car-offer-card">
                                {/* Card Header with Image */}
                                <div className="car-offer-card-header">
                                    <div className="car-image-container">
                                        <img 
                                            src={offer.main_image || '/assets/images/cars/default-car.jpg'} 
                                            alt={offer.make_model}
                                            className="car-image"
                                            onError={(e) => {
                                                e.target.src = '/assets/images/cars/default-car.jpg';
                                            }}
                                        />
                                        <div className="image-overlay">
                                            <button 
                                                className="view-details-btn"
                                                onClick={() => {
                                                    window.location.href = `/car-hire/cars/${offer.id}`;
                                                }}
                                            >
                                                <i className="fas fa-eye"></i> View Details
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Badges */}
                                    {offer.featured && (
                                        <div className="featured-badge">
                                            <i className="fas fa-star"></i> Featured
                                        </div>
                                    )}
                                    
                                    {offer.free_cancellation && (
                                        <div className="free-cancellation-badge">
                                            <i className="fas fa-times-circle"></i> Free Cancellation
                                        </div>
                                    )}
                                </div>
                                
                                {/* Card Body */}
                                <div className="car-offer-card-body">
                                    {/* Car Title and Company */}
                                    <div className="car-title-section">
                                        <h5 className="car-title">{offer.make_model}</h5>
                                        <div className="company-info">
                                            {offer.rental_company?.logo && (
                                                <img 
                                                    src={offer.rental_company.logo} 
                                                    alt={offer.rental_company.name}
                                                    className="company-logo"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            )}
                                            <span className="company-name">{offer.rental_company?.name || 'Car Rental'}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Rating */}
                                    <div className="rating-section">
                                        <div className="rating-stars">
                                            {renderStars(offer.rating)}
                                        </div>
                                        <span className="rating-count">({offer.review_count || 0} reviews)</span>
                                    </div>
                                    
                                    {/* Car Details */}
                                    <div className="car-details-grid">
                                        <div className="detail-item">
                                            <i className="fas fa-users detail-icon"></i>
                                            <span>{offer.car_category?.passenger_capacity || 4} Seats</span>
                                        </div>
                                        <div className="detail-item">
                                            <i className="fas fa-suitcase detail-icon"></i>
                                            <span>{offer.car_category?.luggage_capacity || 2} Bags</span>
                                        </div>
                                        <div className="detail-item">
                                            <i className="fas fa-cog detail-icon"></i>
                                            <span>{offer.car_category?.transmission_display || 'Automatic'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <i className="fas fa-gas-pump detail-icon"></i>
                                            <span>Petrol</span>
                                        </div>
                                    </div>
                                    
                                    {/* Key Features */}
                                    {offer.key_features && offer.key_features.length > 0 && (
                                        <div className="features-section">
                                            {offer.key_features.slice(0, 3).map((feature, idx) => (
                                                <span key={idx} className="feature-tag">
                                                    <i className="fas fa-check-circle"></i> {feature}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Price and Action */}
                                    <div className="card-footer">
                                        <div className="price-section">
                                            <div className="price-info">
                                                <span className="price-label">From</span>
                                                <div className="price-display">
                                                    <span className="price-amount">${formatPrice(offer.base_price_per_day)}</span>
                                                    <span className="price-period">/day</span>
                                                </div>
                                            </div>
                                            <div className="includes-text">
                                                <i className="fas fa-shield-alt"></i> Insurance Included
                                            </div>
                                        </div>
                                        
                                        <button 
                                            className="book-now-btn"
                                            onClick={() => {
                                                window.location.href = `/car-hire/cars/${offer.id}`;
                                            }}
                                        >
                                            <i className="fas fa-calendar-check"></i>
                                            Book Now
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )) : (
                        <div className="col-xl-12">
                            <div className="no-offers-state">
                                <i className="fas fa-car fa-3x text-muted mb-3"></i>
                                <h4>No car rental offers available at the moment</h4>
                                <p>Please check back later for new deals!</p>
                            </div>
                        </div>
                    )}
                </div>
                
                <div className="row mt-5">
                    <div className="col-xl-12 text-center">
                        <a href="/car-hire/search" className="view-all-btn">
                            <i className="fas fa-search"></i>
                            View All Cars
                            <i className="fas fa-chevron-right"></i>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CarHireOffers;
